import React, { useEffect, useState } from 'react';
import Loader from "../../../components/Loader";
import Paginator from "../../../components/Paginator";
import ApiService from "../../../services/ApiService";
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { formatCurrency, formatDate } from "../../../utils/helper";

const History = () => {
    const [isLoader, setIsLoader] = useState(false);
    const [payouts, setPayouts] = useState([]);
    const [pagination, setPagination] = useState({});
    const [apiData, setApiData] = useState({ search: "", status: "", page: 1 });

    useEffect(() => {
        getHistory();
    }, [apiData.page, apiData.search, apiData.status]);

    const getHistory = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({ method: "GET", url: "admin/payouts/history", data: apiData });
            const data = response.data;
            if (data.status) {
                setPayouts(data.data);
                setPagination(data.pagination);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            // handled in ApiService
        } finally {
            setIsLoader(false);
        }
    };

    const handlePageChange = (page) => setApiData(prev => ({ ...prev, page }));
    const clearFilter = () => setApiData({ search: '', status: '', page: 1 });

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Payout History
                        </h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Payout History</li>
                        </ul>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/admin/payouts" className="btn btn-light-primary btn-sm">
                            <i className="bi bi-arrow-left me-1"></i> Partner Payouts
                        </Link>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <div className="row">
                                    <div className="col-md-3">
                                        <input
                                            type="text"
                                            value={apiData.search}
                                            onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })}
                                            className="form-control form-control-solid"
                                            placeholder="Search by payout code..."
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <select
                                            className="form-control form-control-solid"
                                            value={apiData.status}
                                            onChange={e => setApiData({ ...apiData, status: e.target.value, page: 1 })}
                                        >
                                            <option value="">All Status</option>
                                            <option value="Paid">Paid</option>
                                            <option value="Pending">Pending</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2 mt-2">
                                        <button
                                            type="button"
                                            onClick={clearFilter}
                                            className={`btn btn-sm btn-secondary ${!apiData.search && !apiData.status ? 'd-none' : ''}`}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body pt-0">
                            <div className="table-responsive">
                                <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                    <thead>
                                        <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                            <th>Payout Code</th>
                                            <th>Partner</th>
                                            <th>Bookings</th>
                                            <th>Amount Paid</th>
                                            <th>Method</th>
                                            <th>Reference</th>
                                            <th>Status</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody className="fw-semibold text-gray-600 fs-6">
                                        {payouts.length > 0 ? payouts.map((p, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <span className="badge badge-light-primary fs-7">{p.payout_code}</span>
                                                </td>
                                                <td>
                                                    <span className="text-dark fw-bold">{p.partner_id?.company_name || '-'}</span>
                                                </td>
                                                <td>{p.bookings_count}</td>
                                                <td className="fw-bold text-success">{formatCurrency(p.amount)}</td>
                                                <td>{p.method || '-'}</td>
                                                <td>{p.reference || '-'}</td>
                                                <td>
                                                    <span className={`badge ${p.status === 'Paid' ? 'badge-light-success' : 'badge-light-warning'}`}>
                                                        {p.status}
                                                    </span>
                                                </td>
                                                <td>{formatDate(p.paid_at || p.createdAt, 'DD MMM YYYY, hh:mm A')}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="8" className="text-dark text-center mt-2">No data found!</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                {isLoader && <Loader text="Loading" size="30" className="text-center" />}
                                <span>Total Records: {pagination?.total_records}</span>
                                {pagination.total_pages > 1 && (
                                    <Paginator
                                        page={pagination.current_page}
                                        rows={pagination.per_page}
                                        totalRecords={pagination.total_records}
                                        onPageChange={handlePageChange}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default History;
